import { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  formListValueFormatter,
  FormSearchResultAdvanced,
  useDataProvider,
  useLocalization,
  useStore,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import { useNavigate } from 'react-router-dom';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  PageSection,
  getDefaultValueByControl,
} from '@product-framework/index';
import { FORM_VIEW } from '@product-framework/pages/ServiceProvider/constants';
import { email, enabled } from './accessorFunctions';
import listSchema from './list.json';
import searchSchema from './search.json';
import ServiceProviderUserCreate from '../create';
import ServiceProviderUserEdit from '../edit';
import validationSchema from './validations';

function UserList({
  serviceProviderId: pageId,
  onChange,
  action,
  entityId,
  currentIndex,
}) {
  const { formatMessage, messages } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();

  const [resultsFound, setResultsFound] = useState(0);
  const [stackAblePageButtons, setStackAblePageButtons] = useState([]);
  const [showSearch, setShowSearch] = useState();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/users?companyId=${pageId}`;

  const createButton = {
    label: formatMessage({
      id: 'CREATE_NEW_USER_STACKABLE_BUTTON',
      defaultMessage: 'Create User',
    }),
    Icon: IconLibrary.User,
    permissionName: 'CAN_CREATE_SP_USER',
    variant: 'outlined',
    onClick: () => {
      store.domain.list.reset('ServiceProviderUserList');
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.CREATE
        }`,
      );
    },
  };

  const listButton = {
    label: formatMessage({
      id: 'SHOW_SERVICE_PROVIDER_USERS_LIST_STACK_ABLE_BUTTON',
      defaultMessage: 'List Users',
    }),
    Icon: IconLibrary.Users,
    permissionName: 'CAN_LIST_SP_USER',
    variant: 'outlined',
    onClick: () => {
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.LIST
        }`,
      );
    },
  };

  const searchButton = {
    permissionName: 'CAN_SEARCH_SP_USER',
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Show Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Hide Search',
        }),
    Icon: showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    onClick: () => {
      setShowSearch(!showSearch);
    },
  };
  const preProcessSearchRequestData = data => {
    const { id: idArg, status: statusArg, ...other } = data;
    return Object.fromEntries(
      Object.entries({
        status: statusArg === 'true',
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderUserList', searchState);
  };

  const preProcessSearchValues = searchedParams => {
    const formatterMap = {};
    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const searchValueFormatter = ({ name: searchName, value }) => {
    switch (searchName) {
      case 'enabled':
        return value === 'true' ? 'Enabled' : 'Disabled';
      default:
        return null;
    }
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderUserList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      email,
      enabled,
    },
    accessorFunctionsAdditional: {
      navigate: recordId => {
        return `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.EDIT
        }/${recordId}`;
      },
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortBy: 'email',
    sortOrder: 'ASC',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    storeName: listProps.storeName,
    initialValues: {
      status: '',
    },
    getValueControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
    valueFormatter: searchValueFormatter,
    storeName: listProps.storeName,
  };

  useEffect(() => {
    const buttons = [];
    switch (action) {
      case FORM_VIEW.EDIT:
        buttons.push(createButton);
        buttons.push(listButton);
        break;
      case FORM_VIEW.CREATE:
        buttons.push(listButton);
        break;
      default:
        buttons.push(createButton);
        buttons.push(searchButton);
    }

    if (action !== FORM_VIEW.LIST) {
      setShowSearch(false);
    }

    setStackAblePageButtons(buttons);
  }, [action, messages]);

  return (
    <PageSection
      HeaderContent={
        <StackablePageButtons
          backButton={false}
          buttons={stackAblePageButtons}
        />
      }
      label={formatMessage({
        id: 'SERVICE_PROVIDER_USERS_EDIT_HEADING',
        defaultMessage: 'Service Provider User',
      })}>
      <Grid2 container direction="column" spacing={2}>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {action === FORM_VIEW.CREATE && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICE_PROVIDER_USERS_CREATE_HEADING',
              defaultMessage: 'Create New User',
            })}>
            <ServiceProviderUserCreate
              serviceProviderId={pageId}
              currentIndex={currentIndex}
            />
          </PageSection>
        )}

        {action === FORM_VIEW.EDIT && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICE_PROVIDER_USERS_EDIT_HEADING',
              defaultMessage: 'Edit User',
            })}>
            <ServiceProviderUserEdit
              serviceProviderId={pageId}
              userId={entityId}
              onChange={onChange}
            />
          </PageSection>
        )}

        {action === FORM_VIEW.LIST && showSearch && (
          <PageSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'USER_SEARCH_HEADING',
              defaultMessage: 'Search Users',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        {action === FORM_VIEW.LIST && (
          <PageSection
            label={formatMessage({
              id: 'USER_LIST_HEADING',
              defaultMessage: 'Users List',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
                listProps={listProps}
              />
            </Box>
          </PageSection>
        )}
      </Grid2>
    </PageSection>
  );
}

export default observer(UserList);
